import React from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { unitBadges } from '@/lib/units';

/**
 * Tile-header controls driven by a visual's query result:
 *  - Unit badges ($ / % / x …) from `result._measure_units` so viewers know
 *    how a tile's numbers are scaled, with a hover legend key explaining them.
 *  - A one-click "Certify" button (with a quick confirm step) for stewards when
 *    the tile uses an uncertified governed calculation
 *    (`result._measure_provenance`), so they can certify without opening AI
 *    Explore. On success the parent re-runs the visual via `onCertified`
 *    (a certified ratio then flips to a percentage).
 */
export function TileMeasureControls({ result, canCertify, onCertified }) {
  const [certifying, setCertifying] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const badges = unitBadges(result?._measure_units);
  const pending = (result?._measure_provenance || []).filter(
    (p) => p.measure_id && p.kind !== 'certified'
  );

  const handleCertify = async (e) => {
    e?.stopPropagation?.();
    if (!pending.length || certifying) return;
    const reason = window.prompt(
      pending.length > 1
        ? `Reason for certifying ${pending.length} measures (recorded with each version):`
        : `Reason for certifying "${pending[0].label || pending[0].name}" (recorded with this version):`, '');
    if (reason == null || !reason.trim()) return;
    setCertifying(true);
    try {
      for (const p of pending) {
        await api.certifyMeasure(p.measure_id, { reason: reason.trim() });
      }
      toast.success(pending.length > 1 ? `Certified ${pending.length} measures` : `Certified ${pending[0].label || pending[0].name}`);
      onCertified && onCertified();
    } catch (err) {
      toast.error('Certification failed');
    } finally {
      setCertifying(false);
      setConfirming(false);
    }
  };

  if (!badges.length && !(canCertify && pending.length)) return null;

  return (
    <div className="flex items-center gap-1" data-testid="tile-measure-controls">
      {badges.length > 0 && (
        <div className="group relative flex items-center gap-1" data-testid="unit-badge-group">
          {badges.map((b) => (
            <span
              key={b.symbol}
              data-testid={`unit-badge-${b.symbol}`}
              className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded bg-muted/50 border border-border/40 text-[9px] font-semibold text-muted-foreground cursor-help"
            >
              {b.symbol}
            </span>
          ))}
          {/* Hover legend key — explains each unit badge for first-time viewers */}
          <div
            className="absolute top-full right-0 mt-1 z-50 hidden group-hover:block w-max max-w-[200px] rounded-md bg-popover border border-border shadow-lg p-2 pointer-events-none"
            data-testid="unit-badge-legend"
          >
            <p className="text-[8px] uppercase tracking-wide text-muted-foreground/70 mb-1">How values are scaled</p>
            <div className="flex flex-col gap-1">
              {badges.map((b) => (
                <div key={b.symbol} className="flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded bg-muted/50 border border-border/40 text-[9px] font-semibold text-muted-foreground">{b.symbol}</span>
                  <span className="text-[9px] text-foreground">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {canCertify && pending.length > 0 && (
        confirming ? (
          <div className="flex items-center gap-1" data-testid="tile-certify-confirm">
            <button
              onClick={handleCertify}
              disabled={certifying}
              data-testid="tile-certify-confirm-btn"
              title={`Confirm certification: ${pending.map((p) => p.label || p.name).join(', ')}`}
              className="inline-flex items-center gap-1 h-4 px-1.5 rounded bg-amber-500 border border-amber-500 text-[9px] font-semibold text-white hover:bg-amber-600 transition-colors disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5"><path d="M20 6 9 17l-5-5" /></svg>
              {certifying ? 'Certifying…' : 'Confirm'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setConfirming(false); }}
              disabled={certifying}
              data-testid="tile-certify-cancel-btn"
              title="Cancel"
              className="inline-flex items-center justify-center h-4 w-4 rounded bg-muted/60 border border-border/40 text-[9px] font-semibold text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-2 h-2"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
            data-testid="tile-certify-btn"
            title={`Certify: ${pending.map((p) => p.label || p.name).join(', ')}`}
            className="inline-flex items-center gap-1 h-4 px-1.5 rounded bg-amber-500/15 border border-amber-500/40 text-[9px] font-semibold text-amber-500 hover:bg-amber-500/25 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5"><path d="M20 6 9 17l-5-5" /></svg>
            Certify
          </button>
        )
      )}
    </div>
  );
}

export default TileMeasureControls;
